/**
 * Shown when the desktop app declines an explicit logout (desktop only).
 *
 * Logout erases this computer's business data, so it is refused while changes have not
 * reached the server. The user can send them now, export a copy, or cancel. "Log out
 * anyway" only becomes available once a copy has been saved to a file, so unsent work is
 * never discarded without the user holding a copy of it.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LOGOUT_BLOCKED_EVENT, logoutUser } from '../helpers/utilities';

export default function LogoutGuard() {
  const [blocked, setBlocked] = useState(null); // { reason, pending, failed, message }
  const [exported, setExported] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onBlocked = (event) => {
      setBlocked(event.detail || {});
      setExported(false);
      setBusy(false);
    };
    window.addEventListener(LOGOUT_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(LOGOUT_BLOCKED_EVENT, onBlocked);
  }, []);

  if (!blocked) return null;

  const close = () => setBlocked(null);

  if (blocked.reason !== 'unsynced') {
    return (
      <Dialog open onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>Could not log out</DialogTitle>
        <DialogContent dividers>
          <Typography>
            This computer&apos;s data could not be cleared, so you are still signed in. Please try again.
          </Typography>
          {blocked.message && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{blocked.message}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={close}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const count = (blocked.pending || 0) + (blocked.failed || 0);

  const exportUnsent = async () => {
    setBusy(true);
    try {
      const items = (await window.electron.sync.unsent()) || [];
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const saved = await window.electron.saveFile({
        title: 'Export unsent changes',
        defaultName: `younote-unsent-changes-${stamp}.json`,
        filters: [{ name: 'JSON', extensions: ['json'] }],
        data: JSON.stringify({ exportedAt: new Date().toISOString(), items }, null, 2),
      });
      if (saved && !saved.canceled) setExported(true);
    } finally {
      setBusy(false);
    }
  };

  // After sending, try the logout again: it succeeds if everything went through, and
  // otherwise this dialog reopens with the remaining count.
  const syncNow = async () => {
    setBusy(true);
    try {
      await window.electron.sync.now();
    } finally {
      setBlocked(null);
    }
    await logoutUser();
  };

  const logoutAnyway = async () => {
    setBusy(true);
    setBlocked(null);
    await logoutUser(undefined, { acknowledgeDataLoss: true });
  };

  return (
    <Dialog open onClose={busy ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle>Unsent changes on this computer</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <WarningAmberIcon color="warning" />
          <Typography>
            {count} change{count === 1 ? " hasn't" : "s haven't"} reached the server yet. Logging out erases
            this computer&apos;s data, including {count === 1 ? 'it' : 'them'}.
          </Typography>
        </Stack>
        {blocked.failed > 0 && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {blocked.failed} of these could not be sent and need attention in the Sync dialog.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Send them now, or export a copy before logging out.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" disabled={busy} onClick={close}>Cancel</Button>
        <Button disabled={busy} onClick={exportUnsent}>{exported ? 'Exported' : 'Export'}</Button>
        <Button disabled={busy} onClick={syncNow}>Sync now</Button>
        <Button color="error" disabled={busy || !exported} onClick={logoutAnyway}>Log out anyway</Button>
      </DialogActions>
    </Dialog>
  );
}
